// Turns the raw learner-insights payload into the sentences and stat columns
// the Analytics screen leads with. Everything here is derived from fields the
// API already returns — nothing is invented, and every builder returns null (or
// an empty list) when the underlying numbers aren't there yet, so the page can
// simply omit the section instead of rendering a sentence with holes in it.

export function plural(count, singular, pluralForm) {
  return count === 1 ? singular : pluralForm || `${singular}s`;
}

export function formatPercent(fraction) {
  if (fraction == null || Number.isNaN(fraction)) return null;
  return `${Math.round(fraction * 100)}%`;
}

export function formatHour(hour) {
  if (hour == null) return null;
  if (hour === 0) return '12am';
  if (hour === 12) return '12pm';
  return hour > 12 ? `${hour - 12}pm` : `${hour}am`;
}

function sum(values) {
  return values.reduce((total, n) => total + (Number(n) || 0), 0);
}

function round1(n) {
  return Math.round(n * 10) / 10;
}

function weeklyTrend(data) {
  return Array.isArray(data?.weeklyLessonsTrend) ? data.weeklyLessonsTrend : [];
}

// Lessons opened in the two most recent trend buckets, and in the two before
// that. Either can be null when the API hasn't returned enough weeks.
export function fortnightLessons(data) {
  const trend = weeklyTrend(data);
  return {
    recent: trend.length >= 2 ? sum(trend.slice(-2)) : null,
    prior: trend.length >= 4 ? sum(trend.slice(-4, -2)) : null,
  };
}

/**
 * The lead sentence, as segments so the page can bold the headline number.
 * Returns null when there is no activity to describe at all.
 * @returns {{ text: string, strong?: boolean }[] | null}
 */
export function buildLeadNarrative(data) {
  if (!data) return null;

  const { recent, prior } = fortnightLessons(data);
  const parts = [];

  if (recent != null && recent > 0) {
    let opener = "You're building momentum.";
    if (prior != null && prior > 0) {
      if (recent >= prior * 1.2) opener = "You're picking up pace.";
      else if (recent <= prior * 0.8) opener = "You've eased off a little.";
      else opener = "You're keeping a steady pace.";
    }
    parts.push({ text: `${opener} ` });
    parts.push({ text: `${recent} ${plural(recent, 'lesson')} in the last two weeks`, strong: true });
    if (prior != null && prior > 0) {
      parts.push({ text: `, ${recent >= prior ? 'up' : 'down'} from ${prior} in the two weeks before` });
    }
    parts.push({ text: '.' });
  } else if (recent === 0 && prior != null && prior > 0) {
    parts.push({ text: "It's been a quiet couple of weeks. " });
    parts.push({ text: 'No lessons in the last two weeks', strong: true });
    parts.push({ text: `, after ${prior} in the two before.` });
  } else if ((data.lessonsGenerated ?? 0) > 0) {
    parts.push({ text: "Here's where things stand. " });
    parts.push({
      text: `${data.lessonsGenerated} ${plural(data.lessonsGenerated, 'lesson')} built for you so far`,
      strong: true,
    });
    parts.push({ text: '.' });
  } else {
    return null;
  }

  const passed = data.quizzesCompleted ?? 0;
  const average = formatPercent(data.averageQuizScore);
  if (passed > 0 && average) {
    parts.push({ text: ` You're averaging ${average} across ${passed} passed ${plural(passed, 'quiz', 'quizzes')}.` });
  } else if (passed > 0) {
    parts.push({ text: ` You've passed ${passed} ${plural(passed, 'quiz', 'quizzes')}.` });
  }

  const streak = data.currentStreak ?? 0;
  if (streak >= 2) {
    parts.push({ text: ` You're ${streak} days into a streak.` });
  }

  return parts;
}

/**
 * Up to four stat columns for the row beneath the lead sentence. Only metrics
 * the payload actually carries are included, so the row never shows a zero
 * standing in for missing data.
 * @returns {{ value: string, label: string }[]}
 */
export function buildStatColumns(data) {
  if (!data) return [];
  const columns = [];

  const { recent } = fortnightLessons(data);
  if (recent != null) {
    columns.push({ value: String(recent), label: 'Lessons, last two weeks' });
  }

  const average = formatPercent(data.averageQuizScore);
  if (average) {
    columns.push({ value: average, label: 'Average quiz score' });
  }

  if ((data.coursesInProgress ?? 0) > 0) {
    columns.push({ value: String(data.coursesInProgress), label: 'Courses in progress' });
  } else if ((data.coursesCreated ?? 0) > 0) {
    columns.push({ value: String(data.coursesCreated), label: 'Courses created' });
  }

  const streak = data.currentStreak ?? 0;
  if (streak > 0) {
    columns.push({ value: `${streak} ${plural(streak, 'day')}`, label: 'Current streak' });
  } else if ((data.topicsMasteredCount ?? 0) > 0) {
    columns.push({ value: String(data.topicsMasteredCount), label: 'Lessons mastered' });
  }

  return columns.slice(0, 4);
}

/**
 * The "Suggested next" card. Always resolves to something actionable, and the
 * reason it gives is always a number the payload supplied.
 */
export function buildSuggestion(data) {
  if (!data) return null;

  const reviewDue = data.adaptiveV2Enabled ? (data.v2ReviewQueueCount ?? 0) : 0;
  if (reviewDue > 0) {
    return {
      title: 'Clear your review queue',
      body: `${reviewDue} ${plural(reviewDue, 'item')} ${reviewDue === 1 ? 'is' : 'are'} due for review. Spacing them out is what makes them stick.`,
      action: { href: '#review-queue', label: 'Review now' },
    };
  }

  if ((data.coursesInProgress ?? 0) > 0) {
    const count = data.coursesInProgress;
    const tail = data.lastActivity ? `, last opened ${String(data.lastActivity).toLowerCase()}` : '';
    return {
      title: 'Pick up where you left off',
      body: `You have ${count} ${plural(count, 'course')} in progress${tail}. Finishing one adds a line to your transcript.`,
      action: { to: '/dashboard', label: 'Go to your courses' },
    };
  }

  if ((data.coursesCreated ?? 0) > 0) {
    return {
      title: 'Start something new',
      body: "You've finished everything you started. Pick the next thing you want to understand.",
      action: { to: '/course/new', label: 'Create a course' },
    };
  }

  return {
    title: 'Create your first course',
    body: 'Tell Graspd what you want to learn and it builds the lessons and quizzes around you.',
    action: { to: '/course/new', label: 'Create a course' },
  };
}

/**
 * One sentence describing the 12-week activity bars, plus the peak study time
 * when the API knows it. Null when there is nothing to describe.
 */
export function buildActivityNarrative(weeks, options = {}) {
  const list = Array.isArray(weeks) ? weeks : [];
  const counts = list.map((w) => Number(w?.activityCount) || 0);
  if (!counts.length || sum(counts) === 0) return null;

  const activeWeeks = counts.filter((n) => n > 0).length;
  const sentences = [
    `You studied in ${activeWeeks} of the last ${counts.length} ${plural(counts.length, 'week')}.`,
  ];

  if (counts.length >= 6) {
    const recent = counts.slice(-3);
    const earlier = counts.slice(0, -3);
    const recentAvg = round1(sum(recent) / recent.length);
    const earlierAvg = round1(sum(earlier) / earlier.length);
    if (earlierAvg > 0) {
      const verdict =
        recentAvg >= earlierAvg * 1.2
          ? 'ahead of'
          : recentAvg <= earlierAvg * 0.8
            ? 'behind'
            : 'in line with';
      sentences.push(
        `The last three weeks average ${recentAvg} sections a week, ${verdict} the ${earlierAvg} you averaged before that.`
      );
    }
  }

  const hour = formatHour(options.peakLearningHour);
  if (options.peakLearningDay && hour) {
    sentences.push(`Most of it lands on ${options.peakLearningDay}s around ${hour}.`);
  }

  return sentences.join(' ');
}

/**
 * One sentence for the topic-strength card, built from the adaptive mastery
 * scores. Null unless there are at least two topics to compare.
 */
export function buildStrengthNarrative(topics) {
  const list = (Array.isArray(topics) ? topics : []).filter((t) => t && t.masteryScore != null);
  if (list.length < 2) return null;

  const sorted = [...list].sort((a, b) => b.masteryScore - a.masteryScore);
  const best = sorted[0];
  const worst = sorted[sorted.length - 1];
  const bestName = best.topicName || 'Your top topic';
  const worstName = worst.topicName || 'your weakest topic';

  return `${bestName} is your strongest area at ${formatPercent(best.masteryScore)}. ${worstName} has the most room to grow at ${formatPercent(worst.masteryScore)}.`;
}

/**
 * Short label for a week bucket, e.g. "May 18". Falls back to the raw string
 * when the date can't be parsed.
 */
export function formatWeekStart(weekStart) {
  if (!weekStart) return '';
  const date = new Date(`${weekStart}T00:00:00`);
  if (Number.isNaN(date.getTime())) return String(weekStart);
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function weekStartParts(weekStart) {
  const date = new Date(`${weekStart}T00:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  return {
    day: date.getDate(),
    month: date.toLocaleDateString(undefined, { month: 'short' }),
    time: date.getTime(),
  };
}

/** Groups consecutive week buckets by calendar month, for the axis month row. */
export function groupWeeksByMonth(weeks) {
  const groups = [];
  (Array.isArray(weeks) ? weeks : []).forEach((week) => {
    const parts = weekStartParts(week?.weekStart);
    const label = parts ? parts.month : '';
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.span += 1;
    else groups.push({ label, span: 1 });
  });
  return groups;
}
